import React from 'react'
import {
  DataObjectId,
  DeadlineStickerValue,
  GenericStickerState,
  Hub,
  RepeatStickerEntry,
  stickerDeadlineAsStatisticsDatesProperty,
  StickerSettingsInBoard,
  StickerType,
  Tag,
  TimerStickerValue,
  TimetrackerValue
} from 'yougile-model'
import {Accounts} from '@client/accounts'
import {getStickerTitle as getStickerTrackingTitle} from '@components/sticker/sticker-menu/sticker-menu-time-tracking/utils'
import {StickerHub} from '@extensions/sticker/views-web/a-sticker-view'
import {
  getPanelDeadlineTitle,
  getStickerDeadlineTitle
} from '../sticker-menu/sticker-menu-deadline/utils'
import {getStickerStateTitle} from '../sticker-menu/sticker-menu-string/utils'
import {getStickerTimerTitle} from '../sticker-menu/sticker-menu-timer/utils'

interface GetStickerTitleProps {
  sticker: Tag
  stickerType: StickerType
  stickerValue?: GenericStickerState
  stickerDefaultValue?: GenericStickerState
  filterValues?: string[]
  location: string
  task?: Hub
  name: string
  settings?: StickerSettingsInBoard
}

export const getStickerTitle = ({
  sticker,
  stickerType,
  stickerValue,
  stickerDefaultValue,
  filterValues,
  location,
  task,
  name,
  settings
}: GetStickerTitleProps): React.ReactNode => {
  if (location === 'panel') {
    if (!filterValues?.length) {
      if (stickerType === 'deadline') {
        return sticker.properties[stickerDeadlineAsStatisticsDatesProperty]
          ? sticker.title
          : name
      }

      return name
    }

    if (stickerType === 'deadline') {
      return getPanelDeadlineTitle(filterValues)
    }

    if (filterValues.length !== 1) {
      return name
    }

    if (['string', 'sprint'].includes(stickerType)) {
      const filterValue = filterValues[0]
      const stickerValue = filterValue && sticker.states.index[filterValue]

      return stickerValue
        ? getStickerStateTitle(stickerValue, stickerType, name)
        : name
    }

    if (stickerType === 'user') {
      return Accounts.get(filterValues[0] as DataObjectId, true).realName
    }
  }

  if (['text', 'numeric'].includes(stickerType)) {
    const {titleBeforeValue} = settings || {}
    const value = (
      stickerType === 'numeric' && stickerDefaultValue
        ? Number(stickerDefaultValue).toLocaleString('ru-RU')
        : stickerDefaultValue
    ) as string

    if (!value) {
      return name
    }

    return titleBeforeValue ? `${name}: ${value}` : value
  }

  if (stickerType === 'timetracking') {
    const value = stickerValue as TimetrackerValue
    return value?.plan ? getStickerTrackingTitle(value) : '—'
  }

  if (stickerType === 'deadline') {
    if (!task) {
      return name
    }
    return getStickerDeadlineTitle(
      name,
      stickerValue as DeadlineStickerValue | undefined,
      task as StickerHub,
      settings
    )
  }

  if (!stickerValue) {
    return name
  }

  if (['string', 'sprint'].includes(stickerType)) {
    const stateValue = sticker.states.index[stickerValue as DataObjectId]

    return stateValue
      ? getStickerStateTitle(stateValue, stickerType, name)
      : name
  }

  if (['stopwatch', 'timer'].includes(stickerType)) {
    return getStickerTimerTitle(
      task,
      settings?.shouldResetOnMove,
      stickerValue as TimerStickerValue,
      stickerType
    )
  }

  if (stickerType === 'repeat') {
    const value = stickerValue as RepeatStickerEntry
    return `№ ${value.repeatedNumber}`
  }

  return name
}
